import React, { useEffect, useMemo } from 'react';
import {
  Box,
  Heading,
  Text,
  Switch,
  Link,
  RadioGroup,
  Radio,
  Dropdown,
  DropdownOverlay,
  SelectInput,
  ActionList,
  ActionListItem,
  Divider,
  ArrowRightIcon,
} from '@razorpay/blade/components';
import { useFormWithProvider } from 'forms';
import { BladeTooltipWrapper } from 'components/ui/BladeTooltipWrapper/BladeTooltipWrapper';
import { isTaxExempt } from 'components/WizardViews/SalaryComponents/utils';
import { ComplianceBehaviourKey, FlexibleBenefitFormData } from './types';
import { isEmployerNPSComponent } from './utils';
import { getFlexibleComponentTaxSectionsDisplayList } from './utils';
import {
  getWageCalculationToggles,
  DEFAULT_UNCLAIMED_TAXABLE_FROM_MONTH,
  PAYROLL_MONTHS,
} from '../utils';
import useFeature from 'hooks/useFeature';
import { overlayLayers } from 'constants/overlayLayers.generated';

export const TaxDetails = ({ isEditFlow = false }: { isEditFlow?: boolean }) => {
  const { formData, setFormData, errors, initialData } =
    useFormWithProvider<FlexibleBenefitFormData>();
  const showTaxabilitySection = formData?.showInGrossSalary || false;
  const shouldForceHoldUnclaimedAmount = !formData?.showInGrossSalary;
  const isArrearDisabled = !formData?.showInGrossSalary;
  const { isBaseCompensationFloorEnabled } = useFeature('baseCompensationFloor');
  const { isFbpExcludeFromSaAdjustmentEnabled } = useFeature('fbpExcludeFromSaAdjustment');
  const { isFlexiArrearsEnabled } = useFeature('flexiArrears');

  const flexibleBenefitsTaxSectionsDisplayList = useMemo(
    () =>
      getFlexibleComponentTaxSectionsDisplayList({
        taxExemptionSection: initialData?.tax_behaviour?.tax_exemption_section,
        isEditFlow: isEditFlow,
      }),
    [initialData?.tax_behaviour?.tax_exemption_section, isEditFlow],
  );

  const handleTaxBehaviourChange = (field: string, value: string | boolean) => {
    setFormData({
      ...formData,
      tax_behaviour: {
        ...formData.tax_behaviour,
        [field]: value,
      },
    });
  };

  const handleArrearBehaviourChange = (field: string, value: string | boolean) => {
    setFormData({
      ...formData,
      arrear_behaviour: {
        ...formData.arrear_behaviour,
        [field]: value,
      },
    });
  };

  const handleUnclaimedAmountChange = (field: string, value: string | boolean) => {
    if (shouldForceHoldUnclaimedAmount && value === 'false') {
      return;
    }

    setFormData({
      ...formData,
      hold_settings: {
        ...formData.hold_settings,
        [field]: value,
      },
    });
  };

  const handleProrationBehaviourChange = (field: string, value: string | boolean) => {
    setFormData({
      ...formData,
      proration_behaviour: {
        ...formData.proration_behaviour,
        [field]: value,
      },
      tax_behaviour: {
        ...formData.tax_behaviour,
        tax_deduction_behaviour: value ? 'pro_rated' : 'instant',
      },
    });
  };

  const handleComplianceBehaviourChange = (
    behaviourKey: ComplianceBehaviourKey,
    field: string,
    value: boolean,
  ) => {
    setFormData({
      ...formData,
      [behaviourKey]: {
        ...formData[behaviourKey],
        [field]: value,
      },
    });
  };

  const wageCalculationToggles = getWageCalculationToggles(
    formData,
    isBaseCompensationFloorEnabled,
  );

  const visibleWageCalculationToggles = showTaxabilitySection
    ? wageCalculationToggles
    : wageCalculationToggles.filter(
        (toggle) => toggle.behaviourKey === 'base_compensation_behaviour',
      );

  const showExemptionSection = useMemo(
    () =>
      isTaxExempt(
        formData?.tax_behaviour?.tax_exempt_in_old_regime,
        formData?.tax_behaviour?.tax_exempt_in_new_regime,
      ),
    [
      formData?.tax_behaviour?.tax_exempt_in_old_regime,
      formData?.tax_behaviour?.tax_exempt_in_new_regime,
    ],
  );

  const isNPSComponent = isEmployerNPSComponent(formData?.name || '');

  const defaultProrationBehaviour = !!isNPSComponent;

  const isAdjustToSaDisabled =
    isFbpExcludeFromSaAdjustmentEnabled &&
    formData?.sa_adjustment_settings?.adjust_to_sa === false;

  const handleTaxCalculationBasisChange = (value: string) => {
    const basis =
      value === 'declared_amount_by_employee'
        ? 'declared_amount_by_employee'
        : 'approved_amount_by_employer';
    setFormData({
      ...formData,
      tax_behaviour: {
        ...formData.tax_behaviour,
        tax_calculation_basis: {
          basis,
          unclaimed_taxable_from_month:
            basis === 'declared_amount_by_employee'
              ? formData.tax_behaviour?.tax_calculation_basis?.unclaimed_taxable_from_month ??
                DEFAULT_UNCLAIMED_TAXABLE_FROM_MONTH
              : undefined,
        },
      },
    });
  };

  const handleUnclaimedTaxableFromMonthChange = (value: string) => {
    setFormData({
      ...formData,
      tax_behaviour: {
        ...formData.tax_behaviour,
        tax_calculation_basis: {
          ...formData.tax_behaviour?.tax_calculation_basis,
          unclaimed_taxable_from_month: Number(value) || DEFAULT_UNCLAIMED_TAXABLE_FROM_MONTH,
        },
      },
    });
  };

  const isDeclaredBasis =
    formData.tax_behaviour?.tax_calculation_basis?.basis === 'declared_amount_by_employee';

  return (
    <Box padding="spacing.6" display="flex" flexDirection="column" gap="spacing.6">
      <Box>
        <Heading size="large" weight="semibold" color="surface.text.gray.normal">
          Tax & others details
        </Heading>
      </Box>

      {showTaxabilitySection && (
        <Box display="flex" flexDirection="row" gap="spacing.5">
          <Box display="flex" flexDirection="column" gap="spacing.2" width={'50%'}>
            <Heading size="medium" weight="semibold" color="surface.text.gray.normal">
              Taxability
            </Heading>
            <Link
              href="https://razorpay.com/docs/payroll/taxation"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              icon={ArrowRightIcon}
              iconPosition="right">
              Learn more
            </Link>
          </Box>

          <Box display="flex" flexDirection="column" gap="spacing.5" width={'50%'}>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              gap="spacing.4">
              <Text size="medium" weight="regular" color="surface.text.gray.subtle">
                Tax exempted in Old Regime
              </Text>
              <Switch
                isDisabled={isEditFlow && isNPSComponent}
                accessibilityLabel="Toggle tax exemption for Old Regime"
                isChecked={formData?.tax_behaviour?.tax_exempt_in_old_regime || false}
                onChange={({ isChecked }) =>
                  handleTaxBehaviourChange('tax_exempt_in_old_regime', isChecked)
                }
              />
            </Box>

            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              gap="spacing.4">
              <Text size="medium" weight="regular" color="surface.text.gray.subtle">
                Tax exempted in New Regime
              </Text>
              <Switch
                isDisabled={isEditFlow && isNPSComponent}
                accessibilityLabel="Toggle tax exemption for New Regime"
                isChecked={formData?.tax_behaviour?.tax_exempt_in_new_regime || false}
                onChange={({ isChecked }) =>
                  handleTaxBehaviourChange('tax_exempt_in_new_regime', isChecked)
                }
              />
            </Box>

            {showExemptionSection && (
              <Box maxWidth="400px">
                <Dropdown>
                  <SelectInput
                    label="Exemption under Section"
                    placeholder="Select Section"
                    name="taxExemptionSection"
                    value={
                      isAdjustToSaDisabled
                        ? 'section_10_14'
                        : formData.tax_behaviour?.tax_exemption_section
                    }
                    onChange={({ values }) =>
                      handleTaxBehaviourChange('tax_exemption_section', values[0])
                    }
                    validationState={errors.tax_exemption_section ? 'error' : 'none'}
                    errorText={errors.tax_exemption_section}
                    isDisabled={(isEditFlow && isNPSComponent) || isAdjustToSaDisabled}
                    helpText={
                      isAdjustToSaDisabled
                        ? 'Components not adjusted from Special Allowance are always exempted under Section 10(14)'
                        : undefined
                    }
                  />
                  {/*
                    The wizard Modal sits at overlayLayers.modal (1010), above Blade's
                    default dropdownOverlay z-index (1002), so the option list would
                    otherwise render under the modal backdrop.
                  */}
                  <DropdownOverlay zIndex={overlayLayers.nested}>
                    <ActionList>
                      {flexibleBenefitsTaxSectionsDisplayList.map((section) => (
                        <ActionListItem
                          key={section.key}
                          title={section.label}
                          description={section.description}
                          value={section.key}
                        />
                      ))}
                    </ActionList>
                  </DropdownOverlay>
                </Dropdown>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {isAdjustToSaDisabled && (
        <>
          <Divider />

          <Box display="flex" flexDirection="row" gap="spacing.5">
            <Box display="flex" flexDirection="column" gap="spacing.2" width={'50%'}>
              <Heading size="medium" weight="semibold" color="surface.text.gray.subtle">
                Tax Calculation Basis
              </Heading>
              <Text size="small" weight="regular" color="surface.text.gray.muted">
                How the TDS exemption is calculated for this component.
              </Text>
              <Link
                href="https://razorpay.com/docs/payroll/taxation"
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                icon={ArrowRightIcon}
                iconPosition="right">
                Know more
              </Link>
            </Box>

            <Box display="flex" flexDirection="column" gap="spacing.5" width={'50%'}>
              <Box maxWidth="400px">
                <Dropdown>
                  <SelectInput
                    label="Calculation Basis"
                    name="taxCalculationBasis"
                    value={
                      formData.tax_behaviour?.tax_calculation_basis?.basis ||
                      'approved_amount_by_employer'
                    }
                    onChange={({ values }) => handleTaxCalculationBasisChange(values[0])}
                  />
                  <DropdownOverlay zIndex={overlayLayers.nested}>
                    <ActionList>
                      <ActionListItem
                        title="Amount approved by the Employer"
                        description="Approved claim amount is shown as declared and exempted on the payslip."
                        value="approved_amount_by_employer"
                      />
                      <ActionListItem
                        title="Amount declared by the Employee"
                        description="Employee declaration drives the exemption; declared and approved amounts are the same."
                        value="declared_amount_by_employee"
                      />
                    </ActionList>
                  </DropdownOverlay>
                </Dropdown>
              </Box>

              {isDeclaredBasis && (
                <Box maxWidth="400px">
                  <Dropdown>
                    <SelectInput
                      label="Unclaimed amounts taxable from"
                      name="unclaimedTaxableFromMonth"
                      value={String(
                        formData.tax_behaviour?.tax_calculation_basis
                          ?.unclaimed_taxable_from_month ?? DEFAULT_UNCLAIMED_TAXABLE_FROM_MONTH,
                      )}
                      onChange={({ values }) => handleUnclaimedTaxableFromMonthChange(values[0])}
                      helpText="Unclaimed flexi allowances will be taxable from this payroll month onwards — only amounts with approved proofs stay exempted. Defaults to March (financial year end)."
                    />
                    <DropdownOverlay zIndex={overlayLayers.nested}>
                      <ActionList>
                        {PAYROLL_MONTHS.map((month) => (
                          <ActionListItem
                            key={month.value}
                            title={month.label}
                            value={String(month.value)}
                          />
                        ))}
                      </ActionList>
                    </DropdownOverlay>
                  </Dropdown>
                </Box>
              )}
            </Box>
          </Box>
        </>
      )}

      <Divider />

      <>
        <Box display="flex" flexDirection="row" gap="spacing.5">
          <Box display="flex" flexDirection="column" gap="spacing.2" width={'50%'}>
            <Heading size="medium" weight="semibold" color="surface.text.gray.normal">
              Proration & Arrears
            </Heading>
            <Link
              href="https://razorpay.com/docs/payroll/proration-arrears"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              icon={ArrowRightIcon}
              iconPosition="right">
              Learn more
            </Link>
          </Box>

          <Box display="flex" flexDirection="column" gap="spacing.6" width={'50%'}>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="flex-start"
              gap="spacing.4">
              <Box display="flex" flexDirection="column" gap="spacing.2" flex={1}>
                <Text size="medium" weight="regular" color="surface.text.gray.subtle">
                  Component will be prorated
                </Text>
                <Text size="small" weight="regular" color="surface.text.gray.subtle">
                  Amount will be adjusted based on the employee's working period within the pay
                  cycle.
                </Text>
              </Box>
              <Switch
                accessibilityLabel="Toggle component proration"
                isChecked={
                  formData?.proration_behaviour?.support_proration || defaultProrationBehaviour
                }
                onChange={({ isChecked }) =>
                  handleProrationBehaviourChange('support_proration', isChecked)
                }
              />
            </Box>
            {isFlexiArrearsEnabled && (
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="flex-start"
                gap="spacing.4">
                <Box display="flex" flexDirection="column" gap="spacing.2" flex={1}>
                  <Text size="medium" weight="regular" color="surface.text.gray.subtle">
                    Include in arrears
                  </Text>
                  <Text size="small" weight="regular" color="surface.text.gray.muted">
                    Adjust salary for past periods when there were changes, like salary revisions
                    or missed payments.
                  </Text>
                </Box>
                <BladeTooltipWrapper
                  content={
                    isArrearDisabled
                      ? "Enable 'Add to Gross Salary' to include this component in arrears"
                      : ''
                  }
                  zIndex={9999}
                  placement="top-start"
                  tooltipInteractiveWrapperProps={{
                    display: 'flex',
                  }}
                  shouldAddInteractiveWrapper={true}>
                  <Switch
                    accessibilityLabel="Toggle include in arrears"
                    isChecked={formData?.arrear_behaviour?.consider_in_arrear || false}
                    isDisabled={isArrearDisabled}
                    onChange={({ isChecked }) =>
                      handleArrearBehaviourChange('consider_in_arrear', isChecked)
                    }
                  />
                </BladeTooltipWrapper>
              </Box>
            )}
          </Box>
        </Box>
        <Divider />
      </>

      {visibleWageCalculationToggles.length > 0 && (
        <>
          <Box display="flex" flexDirection="row" gap="spacing.5">
            <Box display="flex" flexDirection="column" gap="spacing.2" width={'50%'}>
              <Heading size="medium" weight="semibold" color="surface.text.gray.subtle">
                Wage calculation
              </Heading>
              <Text size="small" weight="regular" color="surface.text.gray.muted">
                Define if the component is included in the calculation of statutory wages.
              </Text>
            </Box>

            <Box display="flex" flexDirection="column" gap="spacing.5" width={'50%'}>
              {visibleWageCalculationToggles.map((toggle) => (
                <Box
                  key={toggle.behaviourKey}
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  gap="spacing.4">
                  <Text size="medium" weight="regular" color="surface.text.gray.subtle">
                    {toggle.label}
                  </Text>
                  <Switch
                    accessibilityLabel={`Toggle ${toggle.label}`}
                    isChecked={toggle.isChecked}
                    onChange={({ isChecked }) =>
                      handleComplianceBehaviourChange(toggle.behaviourKey, toggle.field, isChecked)
                    }
                  />
                </Box>
              ))}
            </Box>
          </Box>
          <Divider />
        </>
      )}

      {!formData?.nonCashBenefit && (
        <>
          <Box display="flex" flexDirection="row" gap="spacing.5">
            <Box display="flex" flexDirection="column" gap="spacing.2" width={'50%'}>
              <Heading size="medium" weight="semibold" color="surface.text.gray.normal">
                Unclaimed amount
              </Heading>
            </Box>

            <Box maxWidth="600px">
              <RadioGroup
                label=""
                name="holdUnclaimedAmount"
                value={
                  shouldForceHoldUnclaimedAmount
                    ? 'true'
                    : formData?.hold_settings?.hold_unclaimed_amount
                }
                onChange={({ value }) =>
                  handleUnclaimedAmountChange('hold_unclaimed_amount', value)
                }
                isDisabled={isEditFlow}>
                <Radio
                  value="true"
                  helpText="Hold unclaimed amount till March month of the financial year">
                  Hold amount
                </Radio>
                <Radio
                  value="false"
                  isDisabled={shouldForceHoldUnclaimedAmount || isEditFlow}
                  helpText="Release unclaimed amount every payroll month as taxable income">
                  Release amount
                </Radio>
              </RadioGroup>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};
